import { Howl } from "howler";


export default class AudioManager
{
    // #region Attributes //

    // private:

    private _arrVolume: Array<number> = [ 1, 0.6, 0.4 ];
    private _arrMute: Array<boolean> = [ false, false, false ];

    private _arrSoundCollection: Array<Map<string, Howl>> = [ new Map<string, Howl>(), new Map<string, Howl>(), new Map<string, Howl>() ];
    private _arrStackPausedHowl: Array<Array<Array<Howl>>> = [ new Array<Array<Howl>>(), new Array<Array<Howl>>(), new Array<Array<Howl>>() ];

    private _curMusicName: string = null; // only one music track is played at a time.

    private _isGlobalMute: boolean = false;

    private _lastHowlId: number = null;
    
    // #endregion //


    // #region Properties //

    public get isMuteSfx(): boolean { return this._arrMute[ track_id.SFX ]; }
    public get isMuteMusic(): boolean { return this._arrMute[ track_id.MUSIC ]; }
    public get isMuteAmbient(): boolean { return this._arrMute[ track_id.AMBIENT ]; }
    public get lastHowlId(): number { return this._lastHowlId; }

    // #endregion //


    // #region Methods //

    // public:

    public load( arrSoundLoadGroup: Array<ISoundLoadGroup> ): void
    {
        for ( let addSoundGroup of arrSoundLoadGroup ) 
        {		
            for ( let soundName of addSoundGroup.arrName )
            {
                // Skip repeated sounds.
                let mapSound: Map<string, Howl> = this._arrSoundCollection[ addSoundGroup.trackId ];
                if ( !mapSound.has( soundName ) )
                {
                    // Create array with full paths including the extensions.
                    const kUrl: string = addSoundGroup.basePath + soundName;
                    let arrUrlWithExtension: Array<string> = new Array<string>();
                    for ( let extension of addSoundGroup.arrExtension ) 
                    {
                        arrUrlWithExtension.push( kUrl + "." + extension );
                    }

                    // Create Howl.
                    let howl: Howl = new Howl( {
                        src : arrUrlWithExtension,
                        autoplay : false,

                        onloaderror : function( soundId: number, error: any ) {
                            /*if ( this._kIsDebugMode )
                            {
                                console.warn( "[AudioManager] Error loading sound:" + soundId + ", with error: " + error );
                            }*/
                        }
                    } );

                    mapSound.set( soundName, howl );
                }
            }
        }
    }

    public removeSounds( arrSoundName: Array<string>, trackId: track_id ): void 
    {
        let mapSound: Map<string, Howl> = this._arrSoundCollection[ trackId ];
        for ( let soundName of arrSoundName ) 
        {
            let howl: Howl = mapSound.get( soundName );
            if ( howl != null )
            {
                // Cleanup the howl.
                if ( howl.playing() )
                {
                    howl.stop();
                }
                howl.unload();
                howl = null;

                // Remove the entry from the map.
                mapSound.delete( soundName );
            }
        }
    }

    public hasSound( soundName: string, trackId: track_id ): boolean 
    {
        return this._arrSoundCollection[ trackId ].has( soundName );
    }

    public isPlayingSound( soundName: string, trackId: track_id ): boolean 
    {
        return this.findPlayingSound( soundName, trackId ) != null;
    }

    public playSfx( name: string, params: IPlayParams = null ) : Howl
    {
        let result: Howl = null;

        if ( !this.hasSound( name, track_id.SFX ) )
        {
            console.warn( "AudioManager.ts :: playSfx() :: cannot find Howl for name: " + name );
        }
        else
        {
            // Check for existing sound.
            result = this.findPlayingSound( name, track_id.SFX );
            if ( result && params && params.restrictRepetition ) 
            {
                if ( result.duration() * result.seek() > 1 )
                {
                    result.play(); // simple way for allowing playing multiple instances of the same sound.
                }
            }
            else
            {
                // Play the howl.
                result = this.play( name, params, track_id.SFX );
            }
        }

        return result;
    }

    public playMusic( name: string, params: IPlayParams = null ) : Howl
    {
        let result: Howl = null;

        // Check for existing sound.
        result = this.findPlayingSound( name, track_id.MUSIC );
        if ( !result && !this._isGlobalMute ) 
        {
            // Play the howl.
            result = this.play( name, params, track_id.MUSIC );
            this.updateSoundMuteStatus( this._arrMute[ track_id.MUSIC ], track_id.MUSIC );
            this._curMusicName = name;
        }

        return result;
    }

    /**
     * Plays an ambient sound.
     * @param {string}      name - the name of a sound.
     * @param {IPlayParams} params - The params for the sound.
     * @returns {Howl}
     */
     public playAmbient( name: string, params: IPlayParams ) : Howl
     {
        let result: Howl = null;
 
        // Check for existing sound.
        result = this.findPlayingSound( name, track_id.AMBIENT );
        if ( !result ) 
        {
            // Play the howl.
            result = this.play( name, params, track_id.AMBIENT );
        }
 
        return result;
     }

    public changeVolume( volume: number, trackId: track_id ): void
    {
        this._arrVolume[ trackId ] = volume;

        let entriesIt: IterableIterator<[string, Howl]> = this._arrSoundCollection[ trackId ].entries();
        for ( let entriesItResult: IteratorResult<[string, Howl]> = entriesIt.next(); !entriesItResult.done; entriesItResult = entriesIt.next() )
        {
            let howl: Howl = entriesItResult.value[ 1 ];
            if ( howl.playing() )
            {
                howl.volume( volume );
            }
        }
    }

    public mute( isMute: boolean, trackId: track_id ): void
    {
        this._arrMute[ trackId ] = isMute;

        this.updateSoundMuteStatus( isMute || this._isGlobalMute, trackId );
    }

    public muteGlobal( isMute: boolean ): void
    {
        this._isGlobalMute = isMute;

        for ( let trackId: track_id = track_id.SFX; trackId <= track_id.AMBIENT; ++trackId )
        {
            this.updateSoundMuteStatus( this._arrMute[ trackId ] || isMute, trackId );
        }
    }

    public stopSfx( name: string = null ): void
    {
        if ( name )
        {
            let howl: Howl = this.findPlayingSound( name, track_id.SFX );
            if ( howl != null )
            {
                howl.stop();
            }
        }
        else
        {
            this._arrSoundCollection[ track_id.SFX ].forEach( ( value: Howl, key: string ) => {
                value.stop();
            } );
        }
    }

    public stopAmbient( name: string = null ): void
    {
        if ( name )
        {
            let howl: Howl = this.findPlayingSound( name, track_id.AMBIENT );
            if ( howl != null )
            {
                howl.stop();
            }
        }
        else
        {
            this._arrSoundCollection[ track_id.AMBIENT ].forEach( ( value: Howl, key: string ) => {
                value.stop();
            } );
        }
    }

    public stopMusic(): void
    {
        if ( this._curMusicName != null )
        {
            // Utils.log( "AudioManager.ts :: fading-out " + this._curMusicName );

            let howl: Howl = this._arrSoundCollection[ track_id.MUSIC ].get( this._curMusicName );
            if ( howl )
            {
                howl.stop();
            }

            this._curMusicName = null;
        }
    }

    public pause( trackId: track_id ): void
    {
        let arrPausedHowl: Array<Howl> = [];
        this._arrSoundCollection[ trackId ].forEach( ( value: Howl, key: string ) => {
            if ( value.playing() )
            {
                value.pause();
                arrPausedHowl.push( value );
            }
        } );
        this._arrStackPausedHowl[ trackId ].push( arrPausedHowl );
    }

    public resume( trackId: track_id ): void
    {
        if ( this._arrStackPausedHowl[ trackId ].length > 0 )
        {
            let arrPausedHowl: Array<Howl> = this._arrStackPausedHowl[ trackId ].pop();
            for ( let howl of arrPausedHowl )
            {
                howl.play();
            }
        }
    }

    public stopPaused( trackId: track_id): void
    {
        if ( this._arrStackPausedHowl[ trackId ].length > 0 )
        {
            let arrPausedHowl: Array<Howl> = this._arrStackPausedHowl[ trackId ].pop();
            for ( let howl of arrPausedHowl )
            {
                howl.stop();
            }
        }
    }

    // private:

    private play( name: string, params: IPlayParams, trackId: track_id ): Howl
    {
        let result: Howl = null;

        // parse the params.
        params = params || {};
        params.randomStart = params.randomStart != undefined ? params.randomStart : false;
        params.loop = params.loop != undefined ? params.loop : false;
        params.volumeRatio = params.volumeRatio != undefined ? params.volumeRatio : 1;
        params.onComplete = params.onComplete || null;
        params.onCompleteScope = params.onCompleteScope || window;

        // Find the howl;
        let mapSound: Map<string, Howl> = this._arrSoundCollection[ trackId ];
        result = mapSound.get( name );
        if ( result )
        {
            // Setup the howl.
            // Volume.
            result.volume( this._arrVolume[ trackId ] * params.volumeRatio );
            result.mute( this._arrMute[ trackId ] || this._isGlobalMute );
            // Random start.
            if ( params.randomStart )
            {
                result.seek( result.duration() * Math.random() );
            }
            // Loop.
            result.loop( params.loop );
            // Mute.
            // Callbacks.
            result.on( "end", function() {
                if ( params.onComplete ) 
                {
                    params.onComplete.call( params.onCompleteScope );
                }
            }.bind( this ) );

            // Play the howl.
            this._lastHowlId = result.play();
        }

        return result;
    }

    private updateSoundMuteStatus( isMute: boolean, trackId: track_id ): void
    {
        let mapSound: Map<string, Howl> = this._arrSoundCollection[ trackId ];
        let entriesIt: IterableIterator<[string, Howl]> = mapSound.entries();
        for ( let entriesItResult: IteratorResult<[string, Howl]> = entriesIt.next(); !entriesItResult.done; entriesItResult = entriesIt.next() )
        {
            let howl: Howl = entriesItResult.value[ 1 ];
            /*if ( howl.playing() )
            {*/
                howl.mute( isMute );
            //}
        }
    }

    private findPlayingSound( name: string, trackId: track_id ): Howl 
    {
        let result: Howl = null;

        let mapSound: Map<string, Howl> = this._arrSoundCollection[ trackId ];
        if ( mapSound.has( name ) )
        {
            const kFoundHowl: Howl = mapSound.get( name );
            if ( kFoundHowl.playing() )
            {
                result = kFoundHowl;
            }
        }

        return result;
    }

    // #endregion //
}

export const enum track_id
{
    SFX = 0,
    MUSIC,
    AMBIENT
}

export interface IPlayParams
{
    randomStart?: boolean;
    loop?: boolean;
    restrictRepetition?: boolean;
    volumeRatio?: number;
    onComplete?: () => void;
    onCompleteScope?: any;
}

export interface ISoundLoadGroup
{
    basePath: string;
    arrName: Array<string>;
    trackId: track_id;
    arrExtension: Array<string>;
}